import { FC, Fragment, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { VideoAnnotation } from "./VideoAnnotation";
import type { PlayerTimecode } from "./playerTimecode";

interface VideoAnnotationsOverlayProps {
  timecodes?: PlayerTimecode[] | null;
  currentTime: number;
  paused: boolean;
  /**
   * Идентичность текущего видео (src). В референсе закрытые вручную
   * аннотации забывались remount'ом VideoScreenContent; наш плеер при смене
   * контента не размонтируется (фулскрин), поэтому сбрасываем явно.
   */
  resetKey?: string | null;
}

// Порт референсного VideoAnnotationsOverlay.js: аннотация активна в окне
// [time, time + duration), закрытая крестиком — не возвращается, на паузе
// текущий набор замораживается. Стекирование — по позиции (углу).
export const VideoAnnotationsOverlay: FC<VideoAnnotationsOverlayProps> = ({
  timecodes,
  currentTime,
  paused,
  resetKey,
}) => {
  const [activeAnnotations, setActiveAnnotations] = useState<PlayerTimecode[]>(
    [],
  );
  const manuallyClosedIds = useRef(new Set<string>());

  const [lastResetKey, setLastResetKey] = useState(resetKey);
  if (lastResetKey !== resetKey) {
    // Синхронный сброс при смене видео — тот же паттерн «adjusting state
    // when a prop changes», что и в useVideoEngine.
    setLastResetKey(resetKey);
    setActiveAnnotations([]);
    manuallyClosedIds.current.clear();
  }

  useEffect(() => {
    const annotations =
      timecodes?.filter(
        (timecode) =>
          timecode.type === "ANNOTATION" && timecode.time !== undefined,
      ) ?? [];

    if (!annotations.length) {
      setActiveAnnotations([]);
      return;
    }

    // При паузе не обновляем список (оставляем текущие аннотации)
    if (paused) {
      return;
    }

    const activeList = annotations.filter((annotation) => {
      const showTime = annotation.time;
      const hideTime = annotation.time + (annotation.duration || 5);
      return (
        currentTime >= showTime &&
        currentTime < hideTime &&
        !manuallyClosedIds.current.has(annotation.id)
      );
    });

    setActiveAnnotations(activeList);
  }, [timecodes, currentTime, paused]);

  if (!activeAnnotations.length) {
    return null;
  }

  // Группируем по позиции для стекирования в пределах одного угла
  const annotationsByPosition = activeAnnotations.reduce<
    Record<string, PlayerTimecode[]>
  >((acc, annotation) => {
    const position = annotation.position ?? "TOP_LEFT";
    (acc[position] ??= []).push(annotation);
    return acc;
  }, {});

  const handleClose = (annotation: PlayerTimecode) => {
    manuallyClosedIds.current.add(annotation.id);
    setActiveAnnotations((prev) =>
      prev.filter((item) => item.id !== annotation.id),
    );
  };

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 200,
      }}
    >
      {Object.entries(annotationsByPosition).map(([position, annotations]) => (
        <Fragment key={position}>
          {annotations.map((annotation, index) => (
            <VideoAnnotation
              key={annotation.id}
              annotation={annotation}
              onClose={() => handleClose(annotation)}
              index={index}
            />
          ))}
        </Fragment>
      ))}
    </Box>
  );
};
